import { type AxiosPromise } from "axios";

export const getDocumentApi = (mainArea: server.dto.MainAreas) => {
  switch (mainArea) {
    case "contract": return window.Api.Contracts;
    case "purchaseAgreement": return window.Api.PurchaseAgreements;
    case "quote": return window.Api.Quotes;
    case "invoice": return window.Api.Invoices;
    default: throw new Error("Unsupported main area: " + mainArea);
  }
};

export const getDocumentCoreApi = (mainArea: server.dto.MainAreas) => {
  switch (mainArea) {
    case "contract": return window.CoreApi.Contract;
    case "purchaseAgreement": return window.CoreApi.PurchaseAgreement;
    case "quote": return window.CoreApi.Quote;
    case "invoice": return window.CoreApi.Invoice;
    default: throw new Error("Unsupported main area: " + mainArea);
  }
};

export const getDocumentCoreApiExceptInvoice = (mainArea: server.dto.MainAreas) => {
  switch (mainArea) {
    case "contract": return window.CoreApi.Contract;
    case "purchaseAgreement": return window.CoreApi.PurchaseAgreement;
    case "quote": return window.CoreApi.Quote;
    default: throw new Error("Unsupported main area: " + mainArea);
  }
};

export const getLinkUnlinkApi = (mainArea: server.dto.DocumentTypes) => {
  const api = getDocumentCoreApi(mainArea);

  return {
    link: api.LinkShows,
    unlink: api.UnlinkShow
  };
}

export const getInvoiceV2EndpointFromLifecycle = (lifecycle: server.dto.DocumentLifeCycles) => {
  switch (lifecycle) {
    case "draft": return window.CoreApi.InvoiceV2.ChangeToDraftStage;
    case "review": return window.CoreApi.InvoiceV2.ChangeToReviewStage;

    // 'pending' lifecycle is handled separately because it requires extra parameters
    default: throw new Error("Unsupported lifecycle for InvoiceV2: " + lifecycle);
  }
};

export const getESignEndpointFromLifecycle = (lifecycle: server.dto.DocumentLifeCycles) => {
  switch (lifecycle) {
    case "draft": return window.CoreApi.ESign.ChangeESignToDraftStage;
    case "review": return window.CoreApi.ESign.ChangeESignToReviewStage;
    case "pending": return window.CoreApi.ESign.ChangeESignToOutForSignature;
    case "discarded": return window.CoreApi.ESign.ChangeESignToDiscardedStage;
  }
};

export const fileApis: { [type in server.dto.MainAreas]?: {

  GetFiles: (dto: server.dto.ListFileInstruction) => AxiosPromise<server.dto.ApiResponseWrapper<server.dto.File[]>>

}
} = {
  show: {
    GetFiles: window.CoreApi.Show.PaginatedFiles
  },
  company: {
    GetFiles: window.CoreApi.Company.PaginatedFiles
  },
  contact: {
    GetFiles: window.CoreApi.Contact.PaginatedFiles
  },
  contract: {
    GetFiles: window.CoreApi.Contract.PaginatedFiles
  },
  invoice: {
    GetFiles: window.CoreApi.Invoice.PaginatedFiles
  },
  purchaseAgreement: {
    GetFiles: window.CoreApi.PurchaseAgreement.PaginatedFiles
  },
  quote: {
    GetFiles: window.CoreApi.Quote.PaginatedFiles
  }
}

export const emailApis: { [type in server.dto.MainAreas]?: {
  Api: {
    PaginatedEmails: (dto: server.dto.ListEmailInstruction) => AxiosPromise<server.dto.ApiResponseWrapper<server.dto.EmailMessage[]>>
    ListRelatedFiles: (itemId: number) => AxiosPromise<server.dto.ApiResponseWrapper<server.dto.RelatedFiles>>
    GetProcessedEmailTemplate: (dto: server.dto.GetProcessedEmailTemplateInstruction) => AxiosPromise<server.dto.ApiResponseWrapper<server.dto.ProcessedEmailTemplate>>
  }
}
} = {
  show: {
    Api: {
      PaginatedEmails: window.CoreApi.Show.PaginatedEmails,
      ListRelatedFiles: window.CoreApi.Show.ListRelatedFiles,
      GetProcessedEmailTemplate: window.CoreApi.Show.GetProcessedEmailTemplate
    }
  },
  company: {
    Api: {
      PaginatedEmails: window.CoreApi.Company.PaginatedEmails,
      ListRelatedFiles: window.CoreApi.Company.ListRelatedFiles,
      GetProcessedEmailTemplate: window.CoreApi.Company.GetProcessedEmailTemplate
    }
  },
  contact: {
    Api: {
      PaginatedEmails: window.CoreApi.Contact.PaginatedEmails,
      ListRelatedFiles: window.CoreApi.Contact.ListRelatedFiles,
      GetProcessedEmailTemplate: window.CoreApi.Contact.GetProcessedEmailTemplate
    }
  },
  contract: {
    Api: {
      PaginatedEmails: window.CoreApi.Contract.PaginatedEmails,
      ListRelatedFiles: window.CoreApi.Contract.ListRelatedFiles,
      GetProcessedEmailTemplate: window.CoreApi.Contract.GetProcessedEmailTemplate
    }
  },
  invoice: {
    Api: {
      PaginatedEmails: window.CoreApi.Invoice.PaginatedEmails,
      ListRelatedFiles: window.CoreApi.Invoice.ListRelatedFiles,
      GetProcessedEmailTemplate: window.CoreApi.Invoice.GetProcessedEmailTemplate
    }
  },
  purchaseAgreement: {
    Api: {
      PaginatedEmails: window.CoreApi.PurchaseAgreement.PaginatedEmails,
      ListRelatedFiles: window.CoreApi.PurchaseAgreement.ListRelatedFiles,
      GetProcessedEmailTemplate: window.CoreApi.PurchaseAgreement.GetProcessedEmailTemplate
    }
  },
  quote: {
    Api: {
      PaginatedEmails: window.CoreApi.Quote.PaginatedEmails,
      ListRelatedFiles: window.CoreApi.Quote.ListRelatedFiles,
      GetProcessedEmailTemplate: window.CoreApi.Quote.GetProcessedEmailTemplate
    }
  }
};

export const downloadFile = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const apiResponse = {
  success: <T>(data: T): server.dto.ApiResponseWrapper<T> => ({ data, hasErrors: false, errors: [], pagination: null }),
  somethingWentWrong: <T extends any[]>(): server.dto.ApiResponseWrapper<T> => ({ data: [] as T, hasErrors: true, errors: [{ message: RESX.GeneralLabels.SomethingUnexpectedHappened, code: "unknown", target: null }], pagination: null }),
}